// src/components/analytics/KPICard.jsx
import React from 'react';
import {
  Box,
  Typography,
  Tooltip,
  useTheme,
  alpha,
} from '@mui/material';
import { 
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { Card } from '../../../design-system';

/**
 * KPI Card Component for displaying analytics metrics
 */
const KPICard = ({
  title,
  value,
  unit = '',
  previousValue = null,
  percentChange = null,
  icon = null,
  tooltip = '',
  color = 'primary',
  magical = false,
  level = 1,
  isLoading = false,
  isError = false,
  errorMessage = '',
  footerText = null,
  onClick = null,
  onRetry = null,
}) => {
  const theme = useTheme();

  // Determine if trend is positive, negative, or neutral
  let trend = null;
  let trendColor = 'inherit';

  if (percentChange !== null) {
    if (percentChange > 0) {
      trend = <TrendingUpIcon fontSize="small" />;
      trendColor = theme.palette.success.main;
    } else if (percentChange < 0) {
      trend = <TrendingDownIcon fontSize="small" />;
      trendColor = theme.palette.error.main;
    } else {
      trend = <TrendingFlatIcon fontSize="small" />;
      trendColor = theme.palette.warning.main;
    }
  }

  // Format value based on magnitude
  const formatValue = (val) => {
    if (val === null || val === undefined) return 'N/A';
    
    if (typeof val === 'number') {
      // Format large numbers
      if (val >= 1000000) {
        return (val / 1000000).toFixed(1) + 'M';
      } else if (val >= 1000) {
        return (val / 1000).toFixed(1) + 'k';
      } else if (val % 1 !== 0) {
        // Handle decimals
        return val.toFixed(1);
      }
    }
    
    return val.toString();
  };

  // Format percent change
  const formatPercentChange = (change) => {
    if (change === null || change === undefined) return '';
    
    const sign = change > 0 ? '+' : '';
    return `${sign}${change.toFixed(1)}%`;
  };

  return (
    <Card
      magical={magical}
      level={level}
      interactive={!!onClick}
      sx={{ 
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.2s',
        '&:hover': onClick ? {
          transform: 'translateY(-4px)',
        } : {},
      }}
      onClick={onClick}
    >
      <Box sx={{ p: 3 }}>
        {/* Title with tooltip */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          {icon && (
            <Box 
              sx={{ 
                mr: 1.5,
                color: theme.palette[color].main,
                display: 'flex',
              }}
            >
              {icon}
            </Box>
          )}
          <Typography 
            variant="subtitle2" 
            color="textSecondary"
            sx={{ 
              flexGrow: 1,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {title}
            {tooltip && (
              <Tooltip title={tooltip}>
                <InfoIcon 
                  fontSize="small" 
                  sx={{ 
                    ml: 0.5, 
                    color: alpha(theme.palette.text.secondary, 0.5),
                    fontSize: 16,
                  }} 
                />
              </Tooltip>
            )}
          </Typography>
        </Box>

        {/* Main value */}
        {isError ? (
          <Box sx={{ mt: 1 }}>
            <Typography 
              variant="h5" 
              component="div"
              sx={{ 
                fontWeight: 500,
                color: theme.palette.error.main,
              }}
            >
              Failed to load
            </Typography>
            <Typography 
              variant="caption" 
              color="error"
              sx={{ 
                display: 'block',
                mt: 0.5,
              }}
            >
              {errorMessage || 'Data unavailable'}
            </Typography>
            {onRetry && (
              <Box 
                onClick={(e) => { e.stopPropagation(); onRetry(); }}
                sx={{ 
                  mt: 1, 
                  display: 'inline-block',
                  color: theme.palette.primary.main,
                  fontWeight: 500,
                  fontSize: '0.75rem',
                  cursor: 'pointer',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                Retry
              </Box>
            )}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
            <Typography 
              variant="h4" 
              component="div"
              sx={{ 
                fontWeight: 600,
                color: isLoading ? alpha(theme.palette.text.primary, 0.3) : theme.palette.text.primary,
              }}
            >
              {isLoading ? '–' : formatValue(value)}
            </Typography>
            {unit && (
              <Typography 
                variant="body2" 
                color="textSecondary"
                sx={{ ml: 0.5 }}
              >
                {unit}
              </Typography>
            )}
          </Box>
        )}

        {/* Percent change */}
        {percentChange !== null && !isLoading && (
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              mt: 1,
              color: trendColor,
            }}
          >
            {trend}
            <Typography 
              variant="body2" 
              sx={{ 
                ml: 0.5,
                fontWeight: 500,
                color: trendColor,
              }}
            >
              {formatPercentChange(percentChange)}
            </Typography>
            {previousValue !== null && (
              <Typography 
                variant="caption" 
                color="textSecondary"
                sx={{ ml: 1 }}
              >
                vs. {formatValue(previousValue)} {unit}
              </Typography>
            )}
          </Box>
        )}

        {/* Footer text */}
        {footerText && (
          <Typography 
            variant="caption" 
            color="textSecondary"
            sx={{ 
              display: 'block',
              mt: 1,
              fontSize: '0.7rem',
            }}
          >
            {footerText}
          </Typography>
        )}
      </Box>
    </Card>
  );
};

export default KPICard;